import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Step, StepLabel, Stepper } from '@mui/material';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import dialogStyles from '../ConfirmDialog/ConfirmDialog.module.css';
import ColorPalette from '../../colorpalette/ColorPalette';
import { RequestCompanyClosureAPI } from '../../api/API';
import { DataProviderInstance } from '../../api/DataProvider';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
    attachmentsEstimateLabel,
    getDownloadParts,
    isExportActive,
    isExportDownloadable,
    latestExportJob,
} from '../company-data-export/exportJob';
import { ClosureConfirmStep, ClosureExportStep, ClosureSummaryStep } from './CompanyClosureSteps';
import { CLOSURE_GRACE_DAYS, closureErrorMessage, formatClosureDate, parseClosureDate } from './companyClosure';

const STEP_LABELS = ['Resumo', 'Exportar', 'Confirmar'];
const DIALOG_MAX_WIDTH = 720;
const DANGER_COLOR = '#dc2626'; // mesmo vermelho do variant danger do ConfirmDialog
const DEFAULT_ERROR = 'Não foi possível encerrar a conta. Tente novamente.';
const EXPORT_ERROR = 'Não foi possível iniciar a exportação. Tente novamente.';
const NO_DOWNLOADS = [];

// Mesmo stepper do PatientDeleteDialog: cinza por padrão, azul no ativo e nos concluídos.
const STEPPER_SX = {
    padding: 0,
    marginTop: '16px',
    marginBottom: '16px',
    '& .MuiStepConnector-line': { borderColor: ColorPalette.border, borderTopWidth: 1.5 },
    '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: ColorPalette.blueMain },
    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: ColorPalette.blueMain },
    '& .MuiStepIcon-root': { color: ColorPalette.blueLight3, fontSize: 24 },
    '& .MuiStepIcon-root.Mui-active': { color: ColorPalette.blueMain },
    '& .MuiStepIcon-root.Mui-completed': { color: ColorPalette.blueMain },
    '& .MuiStepLabel-label': { color: ColorPalette.textLight, fontSize: '0.8125rem' },
    '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': { marginTop: '6px' },
    '& .MuiStepLabel-label.Mui-active': { color: ColorPalette.blueMain, fontWeight: 600 },
    '& .MuiStepLabel-label.Mui-completed': { color: ColorPalette.textDark },
};

// Até quando a conta segue usável e a data prevista da eliminação (estimativa;
// o servidor grava scheduled_at = max(hoje + 90 dias, fim do período pago)).
function useClosurePreview() {
    const { subscription, trialInfo, hasActiveSubscription, isInActiveTrial } = useSubscription();
    const paidUntil = subscription?.has_active_plan ? parseClosureDate(subscription?.next_due_date) : null;
    const trialUntil = isInActiveTrial() ? parseClosureDate(trialInfo?.trial_ends_at) : null;

    let accessUntil;
    if (paidUntil) {
        accessUntil = `A assinatura deixa de ser renovada. A conta continua funcionando até ${formatClosureDate(paidUntil)}, fim do período já pago.`;
    } else if (trialUntil) {
        accessUntil = `A conta continua funcionando até ${formatClosureDate(trialUntil)}, fim do período de teste.`;
    } else if (hasActiveSubscription()) {
        accessUntil = 'A assinatura deixa de ser renovada. A conta continua funcionando até o fim do período já pago.';
    } else {
        accessUntil = 'O acesso ao sistema já está suspenso. Backup e dados continua disponível para exportar.';
    }

    const graceEnd = new Date();
    graceEnd.setHours(0, 0, 0, 0);
    graceEnd.setDate(graceEnd.getDate() + CLOSURE_GRACE_DAYS);
    const scheduled = paidUntil && paidUntil > graceEnd ? paidUntil : graceEnd;

    return { accessUntil, scheduledDate: formatClosureDate(scheduled) };
}

/**
 * Diálogo "Encerrar conta" (LGPD, só o dono) em três passos, no padrão do
 * PatientDeleteDialog: 1. resumo do que acontece e da data prevista;
 * 2. exportação completa iniciada ali mesmo (anexos opcionais, progresso,
 * download ao concluir) com o aviso legal; o Avançar deste passo só libera
 * depois que a pessoa baixou o arquivo nesta abertura do diálogo (todas as
 * partes, se houver mais de uma; o download automático da exportação criada
 * aqui conta como a parte 1); 3. aceites, motivo opcional e nome da empresa
 * digitado.
 * A exportação é a mesma do card "Exportar meus dados": o job criado entra
 * no histórico da página (`onExportCreated` recarrega a lista e marca o job
 * para baixar sozinho ao terminar) e o diálogo acompanha o estado dele por
 * `exportHistory`, que o histórico mantém.
 * Erros aparecem dentro do diálogo: o Toast fica atrás da sobreposição.
 * Especificação: docs/features/lgpd-eliminacao/README.md (checkpoint 4)
 */
function CompanyClosureDialog({ open, companyName, exportHistory, onClose, onDone, onExportCreated }) {
    const { CreateExportJob, GetExportEstimate } = DataProviderInstance();
    const [step, setStep] = useState(1);
    const [exportChecked, setExportChecked] = useState(false);
    const [eliminationChecked, setEliminationChecked] = useState(false);
    const [reason, setReason] = useState('');
    const [typedName, setTypedName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [includeAttachments, setIncludeAttachments] = useState(false);
    const [estimate, setEstimate] = useState(null);
    const [exportStarting, setExportStarting] = useState(false);
    const [exportError, setExportError] = useState(null);
    // Job criado por este diálogo, até o histórico o trazer (e depois, para achá-lo lá).
    const [startedJob, setStartedJob] = useState(null);
    // Partes baixadas nesta abertura do diálogo, por id de job: { [id]: [índices] }.
    const [downloaded, setDownloaded] = useState({});
    // Respostas de uma abertura anterior do diálogo são ignoradas.
    const sessionRef = useRef(0);
    const { accessUntil, scheduledDate } = useClosurePreview();

    // Abrir carrega a estimativa dos anexos; fechar (por qualquer caminho) zera o diálogo.
    useEffect(() => {
        if (open) {
            const session = ++sessionRef.current;
            GetExportEstimate((data) => {
                if (session !== sessionRef.current) return;
                if (data && !data.error) setEstimate(data);
            });
            return;
        }
        sessionRef.current++;
        setStep(1);
        setExportChecked(false);
        setEliminationChecked(false);
        setReason('');
        setTypedName('');
        setSubmitting(false);
        setError(null);
        setIncludeAttachments(false);
        setEstimate(null);
        setExportStarting(false);
        setExportError(null);
        setStartedJob(null);
        setDownloaded({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const markDownloaded = useCallback((jobId, index) => {
        setDownloaded((prev) => {
            const indexes = prev[jobId] || [];
            if (indexes.includes(index)) return prev;
            return { ...prev, [jobId]: [...indexes, index] };
        });
    }, []);

    const expectedName = (companyName || '').trim();
    const nameMatches = expectedName !== '' && typedName.trim() === expectedName;
    const busy = submitting || exportStarting;
    const canSubmit = exportChecked && eliminationChecked && nameMatches && !busy;

    // Exportação mostrada no passo 2: a criada aqui (pelo histórico assim que ele
    // a trouxer; antes disso, como "aguardando") ou, senão, a mais recente da conta.
    const history = Array.isArray(exportHistory) ? exportHistory : [];
    const exportJob = (startedJob && history.find((job) => job.id === startedJob.id))
        || startedJob
        || latestExportJob(history);
    const attachmentsAvailable = (estimate?.attachments?.count || 0) > 0;

    // Avançar do passo 2 só com a exportação concluída e todas as partes baixadas.
    const exportJobId = exportJob?.id;
    const exportReady = isExportDownloadable(exportJob);
    const downloadedIndexes = (exportJobId && downloaded[exportJobId]) || NO_DOWNLOADS;
    const exportDownloaded = exportReady && downloadedIndexes.length >= Math.max(getDownloadParts(exportJob).length, 1);

    // A página baixa sozinha a exportação criada aqui quando ela termina
    // (Export.js, onJobFinished); isso vale como a parte 1 baixada.
    const startedId = startedJob?.id;
    const startedReady = !!startedId && exportJobId === startedId && exportReady;
    useEffect(() => {
        if (startedReady) markDownloaded(startedId, 0);
    }, [startedReady, startedId, markDownloaded]);

    const handleCancel = () => {
        if (busy) return;
        onClose();
    };

    const goTo = (nextStep) => {
        setError(null);
        setStep(nextStep);
    };

    const handleExport = () => {
        if (exportStarting || isExportActive(exportJob)) return;
        const session = sessionRef.current;
        setExportStarting(true);
        setExportError(null);

        CreateExportJob(includeAttachments, (data) => {
            if (session !== sessionRef.current) return;
            setExportStarting(false);
            if (!data || data.error) {
                setExportError(closureErrorMessage(data, EXPORT_ERROR));
                return;
            }
            setStartedJob({
                id: data.exportId,
                status: 'pending',
                progress: 0,
                include_attachments: includeAttachments,
            });
            if (onExportCreated) onExportCreated(data.exportId);
        });
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);

        const payload = { confirmation_name: typedName.trim() };
        if (reason.trim()) payload.reason = reason.trim();

        RequestCompanyClosureAPI(
            payload,
            (response) => {
                setSubmitting(false);
                onClose();
                if (onDone) onDone(response);
            },
            (response) => {
                setSubmitting(false);
                setError(closureErrorMessage(response, DEFAULT_ERROR));
            }
        );
    };

    return (
        <ConfirmDialog
            isOpen={open}
            variant="danger"
            title="Encerrar conta"
            onCancel={handleCancel}
            hideFooter
            maxWidth={DIALOG_MAX_WIDTH}
        >
            <Stepper activeStep={step - 1} alternativeLabel sx={STEPPER_SX}>
                {STEP_LABELS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{ maxHeight: 'calc(100vh - 340px)', minHeight: '120px', overflowY: 'auto', overflowX: 'hidden' }}>
                {step === 1 && <ClosureSummaryStep accessUntil={accessUntil} scheduledDate={scheduledDate} />}
                {step === 2 && (
                    <ClosureExportStep
                        job={exportJob}
                        starting={exportStarting}
                        autoDownload={!!startedJob}
                        error={exportError}
                        includeAttachments={includeAttachments}
                        attachmentsAvailable={attachmentsAvailable}
                        attachmentsLabel={attachmentsEstimateLabel(estimate)}
                        downloadedIndexes={downloadedIndexes}
                        onIncludeAttachmentsChange={setIncludeAttachments}
                        onExport={handleExport}
                        onDownload={(index) => markDownloaded(exportJobId, index)}
                    />
                )}
                {step === 3 && (
                    <ClosureConfirmStep
                        companyName={expectedName}
                        exportChecked={exportChecked}
                        eliminationChecked={eliminationChecked}
                        reason={reason}
                        typedName={typedName}
                        disabled={submitting}
                        onExportChange={setExportChecked}
                        onEliminationChange={setEliminationChecked}
                        onReasonChange={setReason}
                        onTypedNameChange={setTypedName}
                    />
                )}

                {error && <Alert severity="error" sx={{ marginTop: 2 }}>{error}</Alert>}
            </Box>

            <div className={dialogStyles.footer} style={{ margin: '16px -20px -16px' }}>
                {step > 1 && (
                    <button
                        type="button"
                        className={dialogStyles.btnCancel}
                        style={{ marginRight: 'auto' }}
                        onClick={() => goTo(step - 1)}
                        disabled={busy}
                    >
                        Voltar
                    </button>
                )}
                <button type="button" className={dialogStyles.btnCancel} onClick={handleCancel} disabled={busy}>
                    Cancelar
                </button>
                {step < 3 ? (
                    <button
                        type="button"
                        className={dialogStyles.btnConfirm}
                        style={{ background: ColorPalette.blueMain }}
                        onClick={() => goTo(step + 1)}
                        disabled={busy || (step === 2 && !exportDownloaded)}
                    >
                        Avançar
                    </button>
                ) : (
                    <button
                        type="button"
                        className={dialogStyles.btnConfirm}
                        style={{ background: DANGER_COLOR }}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {submitting && <span className={dialogStyles.spinner} />}
                        {submitting ? 'Aguarde...' : 'Encerrar conta'}
                    </button>
                )}
            </div>
        </ConfirmDialog>
    );
}

export default CompanyClosureDialog;
